import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users,
  Eye,
  Pencil,
  Trash2,
  Mail,
  MailCheck,
  UserPlus,
  // UploadCloud, // Import CSV — disabled
  Download,
  Check,
  AlertTriangle,
} from "lucide-react";
import TeamFormWizard from "./TeamFormWizard";
import AddTeamModal from "./AddTeamModal";
// Import CSV is temporarily disabled — see the commented button/modal below.
// import CsvImportModal from "./CsvImportModal";
import useSearchPagination from "../../hooks/useSearchPagination";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import { hasPermission } from "../../utils/permissionUtils";
import {
  T,
  Page,
  PageHeader,
  PrimaryButton,
  SecondaryButton,
  SearchInput,
  Select,
  StatusBadge,
  Avatar,
  IconButton,
  EmptyState,
  Pagination,
  ModalOverlay,
  Card,
  Label,
  Input,
} from "../../components/em/ui";

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const handle401 = (err) => {
  if (err?.response?.status === 401) {
    localStorage.clear();
    window.location.assign("/login");
    return true;
  }
  return false;
};

// The API returns two error shapes: controllers send { error: "text" }, while
// the central envelope in middleware/errorHandler.js sends
// { error: { code, message } }. Dropping the object form straight into a toast
// throws "Objects are not valid as a React child", so always narrow to text.
const apiErrorMsg = (err, fallback) => {
  const e = err?.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object") return e.message || e.code || fallback;
  return fallback;
};

const photoUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  return `${UPLOAD_URL}${p.startsWith("/") ? "" : "/"}${p}`;
};

const fullName = (m) =>
  `${m.first_name || ""} ${m.last_name || ""}`.trim();

const toCsv = (rows, columns) => {
  const escape = (val) => {
    const s = val == null ? "" : String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(c.get(r))).join(","))
    .join("\n");
  return header + "\n" + body;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const PAGE_SIZE = 10;

const TeamManagement = () => {
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  // Same keys the /api/team routes enforce: add / edit / bulk-edit / invite /
  // export is `manage_employees`, removing people is `delete_employees`.
  // Admin holds both through hasPermission.
  const canManage = hasPermission("manage_employees");
  const canDelete = hasPermission("delete_employees");
  // Row checkboxes and the bulk bar serve either key.
  const canSelect = canManage || canDelete;
  const basePath =
    role === "hr" ? "/hr" : role === "member" ? "/member" : "/admin";
  // The logged-in user's linked team_members.id (set at login). Used to pin +
  // highlight their own row and offer self-edit.
  const currentMemberId = (() => {
    try {
      const v = JSON.parse(localStorage.getItem("userdata") || "{}").member_id;
      return v != null ? String(v) : null;
    } catch {
      return null;
    }
  })();
  const isOwnRow = (m) =>
    currentMemberId != null && String(m.id) === currentMemberId;

  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("admin");
  const [activeMember, setActiveMember] = useState(null);

  // The edit wizard and the list share this one route — `showForm` swaps
  // between them, so clicking "Employees" in the nav while the wizard is open
  // navigates to the path we are already on. React Router keeps the same
  // element mounted, `showForm` survives, and the tab appears to do nothing.
  //
  // A same-path click still produces a fresh `location.key` (react-router
  // replaces rather than pushes, and createLocation always mints a key), so
  // that is the signal to fall back to the list.
  const location = useLocation();
  const firstRender = useRef(true);
  const showFormRef = useRef(false);
  showFormRef.current = showForm;
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;   // mount is not a navigation
      return;
    }
    if (!showFormRef.current) return;
    setShowForm(false);
    setActiveMember(null);
    // The component never unmounted, so the list still holds pre-edit rows.
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [openAdd, setOpenAdd] = useState(false);
  // const [openCsv, setOpenCsv] = useState(false); // Import CSV — disabled

  const [selected, setSelected] = useState(new Set());
  const [toast, setToast] = useState(null);
  // Row id whose invitation email is currently in flight (disables that button).
  const [invitingId, setInvitingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Second-stage delete verification: the admin/HR must type the employee's
  // full name before the delete goes through (guards against accidents).
  const [deleteVerify, setDeleteVerify] = useState(null);
  const [deleteNameInput, setDeleteNameInput] = useState("");
  // Second-stage bulk-delete verification. Snapshot of {ids, names} taken when
  // the modal opens, so later selection changes can't alter what gets deleted.
  const [bulkDeleteVerify, setBulkDeleteVerify] = useState(null);
  const [bulkDeleteInput, setBulkDeleteInput] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  // Advanced filters (Phase 3).
  const [managerFilter, setManagerFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  // Bulk edit modal.
  const [bulkEdit, setBulkEdit] = useState(null); // null | { department, employee_status, work_location, primary_manager_id }
  const [bulkEditBusy, setBulkEditBusy] = useState(false);

  const [deptList, setDeptList] = useState([]);
  const [jobTitleList, setJobTitleList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const token = localStorage.getItem("token");

  const deptMap = useMemo(
    () => Object.fromEntries(deptList.map((d) => [String(d.id), d.name])),
    [deptList],
  );
  const jobTitleMap = useMemo(
    () => Object.fromEntries(jobTitleList.map((j) => [String(j.id), j.name])),
    [jobTitleList],
  );
  const statusMap = useMemo(
    () => Object.fromEntries(statusList.map((s) => [String(s.id), s.name])),
    [statusList],
  );

  const designationOf = (m) =>
    jobTitleMap[String(m.jobtitle)] || m.designation || "";
  const deptNameOf = (m) => deptMap[String(m.department)] || "";
  const statusNameOf = (m) => statusMap[String(m.employee_status)] || "";

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/team`, auth());
      setTeam(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (handle401(err)) return;
      setError(err?.response?.data?.error || "Could not load employees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  // Auto-dismiss toasts after 5s. The "Employee added" variant has no Dismiss
  // button — only "Open Profile" — so without this it never went away.
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const authHeader = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token],
  );

  useEffect(() => {
    fetchMasterData();
  }, []); // eslint-disable-line

  const fetchMasterData = async () => {
    try {
      const [dep, jt, st] = await Promise.all([
        axios.get(`${API_URL}/api/settings/departments`, authHeader),
        axios.get(`${API_URL}/api/settings/job-titles`, authHeader),
        axios.get(`${API_URL}/api/settings/employment-statuses`, authHeader),
      ]);
      setDeptList(Array.isArray(dep.data) ? dep.data : []);
      setJobTitleList(Array.isArray(jt.data) ? jt.data : []);
      setStatusList(Array.isArray(st.data) ? st.data : []);
    } catch (err) {
      if (handle401(err)) return;
    }
  };

  // Manager + location option lists derived from the loaded team.
  const nameOf = (m) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email || `#${m.id}`;
  const managerOptions = useMemo(() => {
    const ids = new Set(team.map((m) => m.primary_manager_id).filter((v) => v != null && v !== ""));
    return team
      .filter((m) => ids.has(m.id))
      .map((m) => ({ id: m.id, name: nameOf(m) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [team]);
  const locationOptions = useMemo(
    () => [...new Set(team.map((m) => (m.work_location || "").trim()).filter(Boolean))].sort(),
    [team],
  );

  // ── Bulk edit ───────────────────────────────────────────────────────────
  const submitBulkEdit = async () => {
    const fields = {};
    ["department", "employee_status", "work_location", "primary_manager_id"].forEach((k) => {
      if (bulkEdit[k] !== undefined && bulkEdit[k] !== "") fields[k] = bulkEdit[k];
    });
    if (!Object.keys(fields).length) { setBulkEdit(null); return; }
    setBulkEditBusy(true);
    try {
      const ids = [...selected].map(Number);
      const res = await axios.patch(`${API_URL}/api/team/bulk-edit`, { ids, fields }, auth());
      setToast({ type: "success", msg: `Updated ${res.data?.affected ?? ids.length} employee(s)` });
      setBulkEdit(null);
      setSelected(new Set());
      fetchTeam();
    } catch (err) {
      setToast({ type: "error", msg: err?.response?.data?.error || "Bulk edit failed" });
    } finally {
      setBulkEditBusy(false);
    }
  };

  const filteredTeam = useMemo(() => {
    const list = team.filter((m) => {
      if (
        departmentFilter !== "all" &&
        String(m.department) !== String(departmentFilter)
      )
        return false;
      if (statusFilter && String(m.employee_status) !== String(statusFilter))
        return false;
      if (managerFilter !== "all" && String(m.primary_manager_id) !== String(managerFilter))
        return false;
      if (locationFilter !== "all" && (m.work_location || "").trim() !== locationFilter)
        return false;
      return true;
    });
    // Pin the logged-in user's own row to the very top of the list.
    if (currentMemberId) {
      const idx = list.findIndex((m) => String(m.id) === currentMemberId);
      if (idx > 0) {
        const [self] = list.splice(idx, 1);
        list.unshift(self);
      }
    }
    return list;
  }, [team, departmentFilter, statusFilter, managerFilter, locationFilter, currentMemberId]);

  const { filteredData, currentData, totalPages } = useSearchPagination(
    filteredTeam,
    searchTerm,
    currentPage,
    PAGE_SIZE,
  );

  const handleView = (m) => navigate(`${basePath}/teams/${m.id}`);
  const handleEdit = (m) => {
    setActiveMember(m);
    setFormMode("admin");
    setShowForm(true);
    setSelected(new Set());
  };
  // Self-edit for non-managers goes to the dedicated edit route (guards own id).
  const handleEditOwn = (m) => navigate(`${basePath}/teams/${m.id}/edit`);

  // Re-send the onboarding invitation. The email carries a fresh 48h setup
  // link and no password, so this can never disturb an active login. Patches
  // the row in place rather than refetching the whole list.
  const handleSendInvite = async (m) => {
    setInvitingId(m.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/team/employees/${m.id}/invite`,
        {},
        auth(),
      );
      const stamp = res.data?.invite_sent_at || new Date().toISOString();
      setTeam((prev) =>
        prev.map((row) =>
          row.id === m.id ? { ...row, invite_sent_at: stamp } : row,
        ),
      );
      setToast({ type: "success", msg: `Invitation sent to ${m.email}` });
    } catch (err) {
      if (handle401(err)) return;
      setToast({
        type: "error",
        msg: apiErrorMsg(err, "Could not send the invitation."),
      });
    } finally {
      setInvitingId(null);
    }
  };

  const sentOnLabel = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };

  const softDelete = async (m) => {
    try {
      await axios.delete(`${API_URL}/api/team/${m.id}`, auth());
      setDeleteConfirm(null);
      setDeleteVerify(null);
      setDeleteNameInput("");
      fetchTeam();
    } catch (err) {
      if (handle401(err)) return;
      setToast({ type: "error", msg: err?.response?.data?.error || "Failed to delete employee" });
    }
  };

  // Whitespace-insensitive, case-insensitive name comparison for the
  // type-to-confirm delete gate.
  const normName = (s) => s.trim().replace(/\s+/g, " ").toLowerCase();
  const deleteNameMatches =
    deleteVerify && normName(deleteNameInput) === normName(fullName(deleteVerify));

  // Open the type-to-confirm modal. Snapshot the selected ids + names now so
  // the confirmed set is frozen even if the underlying selection changes.
  const handleBulkDelete = () => {
    if (!canDelete || !selected.size) return;
    const ids = Array.from(selected);
    const names = ids
      .map((id) => {
        const m = team.find((t) => t.id === id);
        return m ? fullName(m) || m.email : `#${id}`;
      });
    setBulkDeleteInput("");
    setBulkDeleteVerify({ ids, names });
  };

  const closeBulkDelete = () => {
    setBulkDeleteVerify(null);
    setBulkDeleteInput("");
  };

  const confirmBulkDelete = async () => {
    if (!bulkDeleteVerify) return;
    try {
      await axios.delete(`${API_URL}/api/team/bulk`, {
        ...auth(),
        data: { ids: bulkDeleteVerify.ids },
      });
      closeBulkDelete();
      setSelected(new Set());
      fetchTeam();
    } catch (err) {
      if (handle401(err)) return;
      setToast({ type: "error", msg: err?.response?.data?.error || "Bulk delete failed" });
    }
  };

  // Exact phrase the admin must type to arm the bulk delete, e.g.
  // "DELETE 3 EMPLOYEES" (singular "EMPLOYEE" for one).
  const bulkPhrase = bulkDeleteVerify
    ? `DELETE ${bulkDeleteVerify.ids.length} ${
        bulkDeleteVerify.ids.length === 1 ? "EMPLOYEE" : "EMPLOYEES"
      }`
    : "";
  const bulkPhraseMatches =
    !!bulkDeleteVerify && bulkDeleteInput.trim() === bulkPhrase;

  const exportCsv = (rows) => {
    const cols = [
      { label: "First Name", get: (r) => r.first_name },
      { label: "Last Name", get: (r) => r.last_name },
      { label: "Email", get: (r) => r.email },
      { label: "Designation", get: (r) => designationOf(r) },
      { label: "Department", get: (r) => deptNameOf(r) },
      { label: "Employee ID", get: (r) => r.employeeid },
      { label: "Status", get: (r) => statusNameOf(r) },
      { label: "Phone", get: (r) => r.contact_number },
    ];
    downloadCsv(`employees-${Date.now()}.csv`, toCsv(rows, cols));
  };

  const handleFormClose = () => {
    setActiveMember(null);
    setShowForm(false);
    fetchTeam();
  };
  const handleAddCreated = ({ id, name, email }) => {
    setToast({
      id,
      name,
      email,
      // Nothing is copied or emailed automatically any more — point them at
      // the row action that actually sends it.
      message: "Use the mail icon on their row to send the invitation.",
    });
    setOpenAdd(false);
    fetchTeam();
  };
  const openNewEmployee = async (id) => {
    setToast(null);
    if (!id) return;
    let target = team.find((t) => t.id === id);
    if (!target) {
      try {
        target = (await axios.get(`${API_URL}/api/team/${id}`, auth())).data;
      } catch {
        await fetchTeam();
        target = team.find((t) => t.id === id);
      }
    }
    if (target) handleEdit(target);
  };

  const toggleRow = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allSelected =
    currentData.length > 0 && currentData.every((m) => selected.has(m.id));
  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) currentData.forEach((m) => next.delete(m.id));
      else currentData.forEach((m) => next.add(m.id));
      return next;
    });
  const bulkRows = [...selected]
    .map((id) => team.find((e) => e.id === id))
    .filter(Boolean);

  // Full-page form (edit/view) — unchanged behaviour.
  if (showForm) {
    return (
      <div className="management-section employee-wrap">
        <TeamFormWizard
          editData={activeMember}
          mode={formMode}
          onClose={handleFormClose}
          onEdit={() => setFormMode("admin")}
        />
      </div>
    );
  }

  const COLS = [
    { key: "name", label: "Name", grow: 1.7, min: 190 },
    { key: "designation", label: "Designation", w: 150 },
    { key: "status", label: "Status", w: 120 },
    { key: "email", label: "Email", grow: 1.3, min: 170 },
    { key: "phone", label: "Phone", w: 130 },
    { key: "actions", label: "", w: 156 },
  ];
  // Mixed flex + fixed-px so fixed columns never get squeezed and the
  // right-most actions cell is always visible (no overflow clipping).
  const colStyle = (c) =>
    c.grow
      ? { flex: `${c.grow} 1 0`, minWidth: c.min }
      : { width: c.w, flexShrink: 0 };
  // Minimum width the row needs before the Actions column would be clipped.
  // The table body scrolls horizontally below this, so the icons stay reachable
  // on narrow content areas instead of being cut off by overflow:hidden.
  const TABLE_MIN_W =
    (canSelect ? 32 : 0) + 190 + 150 + 120 + 170 + 130 + 124 + 60;

  return (
    <Page>
      <PageHeader
        icon={Users}
        title="Employees"
        subtitle={`${team.length} ${team.length === 1 ? "employee" : "employees"}`}
        actions={
          canManage && (
            <>
              <SecondaryButton
                icon={Download}
                onClick={() => exportCsv(filteredData)}
              >
                Export CSV
              </SecondaryButton>
              {/* Import CSV — temporarily disabled. Re-enable by uncommenting
                  this button plus the `openCsv` state, the CsvImportModal
                  import, and the modal render further down.
              <SecondaryButton
                icon={UploadCloud}
                onClick={() => setOpenCsv(true)}
              >
                Import CSV
              </SecondaryButton>
              */}
              <PrimaryButton icon={UserPlus} onClick={() => setOpenAdd(true)}>
                Add Employee
              </PrimaryButton>
            </>
          )
        }
      />

      {/* Bulk action bar */}
      {selected.size > 0 && canSelect && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            borderRadius: T.rLg,
            background: T.bgBlack,
            marginBottom: 12,
            color: "#fff",
          }}
        >
          <Check size={16} />
          <span style={{ font: `600 13px ${T.font}` }}>
            {selected.size} selected
          </span>
          <div style={{ flex: 1 }} />
          {canManage && (
          <button
            onClick={() => setBulkEdit({ department: "", employee_status: "", work_location: "", primary_manager_id: "" })}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
              font: `500 12px ${T.font}`,
            }}
          >
            <Pencil size={13} /> Edit selected
          </button>
          )}
          <button
            onClick={() => exportCsv(bulkRows)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
              font: `500 12px ${T.font}`,
            }}
          >
            <Download size={13} /> Export selected
          </button>
          {canDelete && (
          <button
            onClick={handleBulkDelete}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid rgba(231,0,11,0.4)",
              background: "rgba(231,0,11,0.12)",
              color: "#ff7a85",
              cursor: "pointer",
              font: `500 12px ${T.font}`,
            }}
          >
            <Trash2 size={13} /> Delete selected
          </button>
          )}
          <button
            onClick={() => setSelected(new Set())}
            style={{
              background: "transparent",
              border: 0,
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
              font: `500 12px ${T.font}`,
              padding: "4px 8px",
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Search + filters */}
      <div
        style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}
      >
        <SearchInput
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search name, email, designation…"
          style={{ flex: 1, minWidth: 220 }}
        />
        <Select
          value={departmentFilter}
          onChange={(e) => {
            setDepartmentFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: "auto", minWidth: 170 }}
        >
          <option value="all">All departments</option>
          {deptList.map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.name}
            </option>
          ))}
        </Select>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: "auto", minWidth: 150 }}
        >
          <option value="">All statuses</option>
          {statusList.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </Select>
        {managerOptions.length > 0 && (
          <Select
            value={managerFilter}
            onChange={(e) => { setManagerFilter(e.target.value); setCurrentPage(1); }}
            style={{ width: "auto", minWidth: 160 }}
          >
            <option value="all">All managers</option>
            {managerOptions.map((m) => (
              <option key={m.id} value={String(m.id)}>Reports to {m.name}</option>
            ))}
          </Select>
        )}
        {locationOptions.length > 0 && (
          <Select
            value={locationFilter}
            onChange={(e) => { setLocationFilter(e.target.value); setCurrentPage(1); }}
            style={{ width: "auto", minWidth: 150 }}
          >
            <option value="all">All locations</option>
            {locationOptions.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </Select>
        )}
      </div>

      {/* Table */}
      <div
        style={{
          background: "var(--em-bg-card)",
          borderRadius: 14,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          boxShadow: T.shadowSm,
        }}
      >
        <div style={{ overflowX: "auto" }}>
          {/* Head */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              background: T.bgHead,
              borderBottom: `1px solid ${T.border}`,
              height: 40,
              minWidth: TABLE_MIN_W,
            }}
          >
            {canSelect && (
              <div
                style={{
                  width: 32,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAllPage}
                  style={{ accentColor: T.orange, cursor: "pointer" }}
                />
              </div>
            )}
            {COLS.map((c) => (
              <div key={c.key} style={{ ...colStyle(c), paddingRight: 8 }}>
                <span
                  style={{
                    font: `600 11px ${T.font}`,
                    color: T.fg5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {c.label}
                </span>
              </div>
            ))}
          </div>

          {/* Body */}
          {loading ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: T.fg5,
                font: `400 14px ${T.font}`,
              }}
            >
              Loading employees…
            </div>
          ) : error ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: T.danger,
                font: `400 14px ${T.font}`,
              }}
            >
              ⚠ {error}
            </div>
          ) : currentData.length === 0 ? (
            <EmptyState
              icon={Users}
              title={
                team.length === 0
                  ? "No employees yet"
                  : "No employees match your filters"
              }
              subtitle={
                team.length === 0
                  ? "Add your first employee or import via CSV."
                  : "Try clearing filters or searching for something else."
              }
              action={
                canManage &&
                team.length === 0 && (
                  <PrimaryButton
                    icon={UserPlus}
                    onClick={() => setOpenAdd(true)}
                  >
                    Add Employee
                  </PrimaryButton>
                )
              }
            />
          ) : (
            currentData.map((m, i) => {
              const isSelected = selected.has(m.id);
              const own = isOwnRow(m);
              return (
                <div
                  key={m.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    height: 56,
                    background: isSelected
                      ? T.orange50
                      : own
                        ? T.orange50
                        : i % 2 === 0
                          ? T.bgCard
                          : T.bgZebra,
                    borderBottom: `1px solid ${T.divider}`,
                    transition: "background 100ms",
                    minWidth: TABLE_MIN_W,
                    boxShadow: own ? `inset 3px 0 0 ${T.orange}` : "none",
                  }}
                >
                  {canSelect && (
                    <div
                      style={{
                        width: 32,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(m.id)}
                        style={{ accentColor: T.orange, cursor: "pointer" }}
                      />
                    </div>
                  )}
                  {/* Name + dept */}
                  <div
                    style={{
                      ...colStyle(COLS[0]),
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      paddingRight: 8,
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleView(m)}
                  >
                    <Avatar
                      name={fullName(m)}
                      photo={photoUrl(m.profile_photo)}
                      size={36}
                      gradient
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            font: `600 13px ${T.font}`,
                            color: T.fg1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {fullName(m) || "—"}
                          {}
                        </span>
                        {own && (
                          <span
                            style={{
                              flexShrink: 0,
                              padding: "1px 7px",
                              borderRadius: 9999,
                              background: T.orange,
                              color: "#fff",
                              font: `700 9px ${T.font}`,
                              letterSpacing: "0.04em",
                            }}
                          >
                            YOU
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          font: `400 11px ${T.font}`,
                          color: T.fg7,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {deptNameOf(m) || "—"}
                      </div>
                    </div>
                  </div>
                  {/* Designation */}
                  <div
                    style={{
                      ...colStyle(COLS[1]),
                      paddingRight: 8,
                      font: `400 12px ${T.font}`,
                      color: T.fg3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {designationOf(m) || "—"}
                  </div>
                  {/* Status */}
                  <div style={{ ...colStyle(COLS[2]), paddingRight: 8 }}>
                    <StatusBadge label={statusNameOf(m)} />
                  </div>
                  {/* Email */}
                  <div
                    style={{
                      ...colStyle(COLS[3]),
                      paddingRight: 8,
                      font: `400 12px ${T.font}`,
                      color: T.fg4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.email}
                  </div>
                  {/* Phone */}
                  <div
                    style={{
                      ...colStyle(COLS[4]),
                      paddingRight: 8,
                      font: `400 12px ${T.font}`,
                      color: T.fg5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.contact_number || "—"}
                  </div>
                  {/* Actions */}
                  <div
                    style={{
                      ...colStyle(COLS[5]),
                      display: "flex",
                      gap: 4,
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton
                      icon={Eye}
                      title="View profile"
                      onClick={() => handleView(m)}
                    />
                    {canManage && (
                      <IconButton
                        icon={Pencil}
                        title="Edit"
                        onClick={() => handleEdit(m)}
                      />
                    )}
                    {!canManage && own && (
                      <IconButton
                        icon={Pencil}
                        title="Edit my profile"
                        onClick={() => handleEditOwn(m)}
                      />
                    )}
                    {canManage && (
                      <IconButton
                        icon={m.invite_sent_at ? MailCheck : Mail}
                        disabled={invitingId === m.id}
                        title={
                          m.invite_sent_at
                            ? `Invitation sent ${sentOnLabel(m.invite_sent_at)} — click to resend`
                            : "Send invitation email"
                        }
                        onClick={() => handleSendInvite(m)}
                        style={{
                          opacity: invitingId === m.id ? 0.5 : 1,
                          ...(m.invite_sent_at
                            ? {
                                color: T.success600,
                                border: "1px solid rgba(0,166,62,0.25)",
                                background: "rgba(0,166,62,0.07)",
                              }
                            : null),
                        }}
                      />
                    )}
                    {canDelete && (
                      <IconButton
                        icon={Trash2}
                        tone="danger"
                        title="Delete"
                        onClick={() => setDeleteConfirm(m)}
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {!loading && !error && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          total={filteredData.length}
          pageSize={PAGE_SIZE}
          onPage={setCurrentPage}
        />
      )}

      {/* Delete confirm banner */}
      {deleteConfirm && (
        <div
          style={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            background: T.bgBlack,
            color: "#fff",
            padding: "14px 20px",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 14,
            boxShadow: "0 12px 32px -8px rgba(0,0,0,0.5)",
            zIndex: 300,
            minWidth: 360,
          }}
        >
          <AlertTriangle size={18} color="#fbbf24" style={{ flexShrink: 0 }} />
          <span style={{ font: `500 13px ${T.font}`, flex: 1 }}>
            Soft-delete{" "}
            <strong>{fullName(deleteConfirm) || deleteConfirm.email}</strong>?
            Record is retained and can be restored.
          </span>
          <button
            onClick={() => {
              setDeleteVerify(deleteConfirm);
              setDeleteNameInput("");
              setDeleteConfirm(null);
            }}
            style={{
              padding: "6px 14px",
              borderRadius: 8,
              background: T.danger,
              color: "#fff",
              border: 0,
              cursor: "pointer",
              font: `600 12px ${T.font}`,
            }}
          >
            Delete
          </button>
          <button
            onClick={() => setDeleteConfirm(null)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: 0,
              cursor: "pointer",
              font: `500 12px ${T.font}`,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Second-stage delete verification: type the full name to confirm */}
      {deleteVerify && (
        <ModalOverlay
          onClose={() => {
            setDeleteVerify(null);
            setDeleteNameInput("");
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.bgCard,
              borderRadius: 16,
              padding: 24,
              width: 420,
              maxWidth: "100%",
              boxShadow: "0 24px 64px -16px rgba(0,0,0,0.4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <AlertTriangle size={20} color={T.danger} style={{ flexShrink: 0 }} />
              <h3 style={{ margin: 0, font: `700 16px ${T.font}`, color: T.fg1, flex: 1 }}>
                Confirm employee deletion
              </h3>
              <button
                onClick={() => {
                  setDeleteVerify(null);
                  setDeleteNameInput("");
                }}
                aria-label="Close"
                style={{
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: T.fg3,
                  font: `600 16px ${T.font}`,
                  lineHeight: 1,
                  padding: 4,
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ margin: "0 0 14px", font: `400 13px ${T.font}`, color: T.fg2 }}>
              This is a protected action. To delete{" "}
              <strong>{fullName(deleteVerify) || deleteVerify.email}</strong>,
              type the employee's full name (first and last name) exactly as
              shown below. The record is retained and can be restored.
            </p>
            <div
              style={{
                font: `600 13px ${T.font}`,
                color: T.fg1,
                background: T.bgHead,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 10,
                userSelect: "none",
              }}
            >
              {fullName(deleteVerify)}
            </div>
            <input
              autoFocus
              value={deleteNameInput}
              onChange={(e) => setDeleteNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && deleteNameMatches)
                  softDelete(deleteVerify);
                if (e.key === "Escape") {
                  setDeleteVerify(null);
                  setDeleteNameInput("");
                }
              }}
              placeholder="Type the full name to confirm"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${deleteNameMatches ? T.danger : T.border}`,
                font: `500 13px ${T.font}`,
                color: T.fg1,
                background: T.bgCard,
                outline: "none",
                marginBottom: 16,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => {
                  setDeleteVerify(null);
                  setDeleteNameInput("");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  background: "transparent",
                  color: T.fg2,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  font: `500 13px ${T.font}`,
                }}
              >
                Cancel
              </button>
              <button
                disabled={!deleteNameMatches}
                onClick={() => softDelete(deleteVerify)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  background: T.danger,
                  color: "#fff",
                  border: 0,
                  cursor: deleteNameMatches ? "pointer" : "not-allowed",
                  opacity: deleteNameMatches ? 1 : 0.45,
                  font: `600 13px ${T.font}`,
                }}
              >
                Delete employee
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {bulkDeleteVerify && (
        <ModalOverlay onClose={closeBulkDelete}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.bgCard,
              borderRadius: 16,
              padding: 24,
              width: 460,
              maxWidth: "100%",
              boxShadow: "0 24px 64px -16px rgba(0,0,0,0.4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <AlertTriangle size={20} color={T.danger} style={{ flexShrink: 0 }} />
              <h3 style={{ margin: 0, font: `700 16px ${T.font}`, color: T.fg1, flex: 1 }}>
                Delete {bulkDeleteVerify.ids.length} employee
                {bulkDeleteVerify.ids.length === 1 ? "" : "s"}?
              </h3>
              <button
                onClick={closeBulkDelete}
                aria-label="Close"
                style={{
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: T.fg3,
                  font: `600 16px ${T.font}`,
                  lineHeight: 1,
                  padding: 4,
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ margin: "0 0 10px", font: `400 13px ${T.font}`, color: T.fg2 }}>
              This is a protected bulk action. The following records will be
              soft-deleted (retained and restorable):
            </p>
            <div
              style={{
                font: `500 12.5px ${T.font}`,
                color: T.fg1,
                background: T.bgHead,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 12,
                maxHeight: 96,
                overflowY: "auto",
              }}
            >
              {bulkDeleteVerify.names.slice(0, 5).join(", ")}
              {bulkDeleteVerify.names.length > 5
                ? ` and ${bulkDeleteVerify.names.length - 5} more`
                : ""}
            </div>
            <p style={{ margin: "0 0 8px", font: `400 13px ${T.font}`, color: T.fg2 }}>
              Type <strong>{bulkPhrase}</strong> to confirm.
            </p>
            <input
              autoFocus
              value={bulkDeleteInput}
              onChange={(e) => setBulkDeleteInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && bulkPhraseMatches) confirmBulkDelete();
                if (e.key === "Escape") closeBulkDelete();
              }}
              placeholder={bulkPhrase}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${bulkPhraseMatches ? T.danger : T.border}`,
                font: `500 13px ${T.font}`,
                color: T.fg1,
                background: T.bgCard,
                outline: "none",
                marginBottom: 16,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={closeBulkDelete}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  background: "transparent",
                  color: T.fg2,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  font: `500 13px ${T.font}`,
                }}
              >
                Cancel
              </button>
              <button
                disabled={!bulkPhraseMatches}
                onClick={confirmBulkDelete}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  background: T.danger,
                  color: "#fff",
                  border: 0,
                  cursor: bulkPhraseMatches ? "pointer" : "not-allowed",
                  opacity: bulkPhraseMatches ? 1 : 0.45,
                  font: `600 13px ${T.font}`,
                }}
              >
                Delete {bulkDeleteVerify.ids.length} employee
                {bulkDeleteVerify.ids.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {openAdd && (
        <AddTeamModal
          onClose={() => setOpenAdd(false)}
          onCreated={handleAddCreated}
        />
      )}
      {/* Import CSV — temporarily disabled, see the button above.
      {openCsv && (
        <CsvImportModal
          open={openCsv}
          onClose={() => setOpenCsv(false)}
          onImported={() => fetchTeam()}
        />
      )}
      */}

      {bulkEdit && (
        <ModalOverlay onClose={() => setBulkEdit(null)}>
          <Card onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "100%", padding: 24 }}>
            <div style={{ font: `700 17px ${T.font}`, color: T.fg1, marginBottom: 4 }}>
              Edit {selected.size} employee{selected.size === 1 ? "" : "s"}
            </div>
            <div style={{ font: `400 12.5px ${T.font}`, color: T.fg5, marginBottom: 18 }}>
              Only fields you set below are changed. Leave a field blank to keep it as-is.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <Label>Department</Label>
                <Select value={bulkEdit.department} onChange={(e) => setBulkEdit((p) => ({ ...p, department: e.target.value }))}>
                  <option value="">— Keep unchanged —</option>
                  {deptList.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={bulkEdit.employee_status} onChange={(e) => setBulkEdit((p) => ({ ...p, employee_status: e.target.value }))}>
                  <option value="">— Keep unchanged —</option>
                  {statusList.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </Select>
              </div>
              <div>
                <Label>Manager</Label>
                <Select value={bulkEdit.primary_manager_id} onChange={(e) => setBulkEdit((p) => ({ ...p, primary_manager_id: e.target.value }))}>
                  <option value="">— Keep unchanged —</option>
                  {team.filter((m) => !selected.has(m.id)).map((m) => (
                    <option key={m.id} value={String(m.id)}>{nameOf(m)}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Work location</Label>
                <Input
                  value={bulkEdit.work_location}
                  onChange={(e) => setBulkEdit((p) => ({ ...p, work_location: e.target.value }))}
                  placeholder="e.g. Bangalore (leave blank to keep)"
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <SecondaryButton type="button" onClick={() => setBulkEdit(null)}>Cancel</SecondaryButton>
              <PrimaryButton icon={Pencil} onClick={submitBulkEdit} disabled={bulkEditBusy}>
                {bulkEditBusy ? "Applying…" : "Apply changes"}
              </PrimaryButton>
            </div>
          </Card>
        </ModalOverlay>
      )}

      {toast && toast.msg && (
        <>
          <div className="team-toast-backdrop" onClick={() => setToast(null)} />
          <div className="team-toast" role="status" aria-live="polite">
            <div className="icon" style={toast.type === "error" ? { background: "rgba(231,0,11,0.12)", color: "#e7000b" } : undefined}>
              <Check size={14} />
            </div>
            <div className="toast-body">
              <span className="toast-title">{toast.msg}</span>
            </div>
            <div className="toast-actions">
              <button className="toast-btn" onClick={() => setToast(null)}>Dismiss</button>
            </div>
          </div>
        </>
      )}
      {toast && toast.name && (
        <>
          <div className="team-toast-backdrop" />
          <div className="team-toast" role="status" aria-live="polite">
            <div className="icon">
              <Check size={14} />
            </div>
            <div className="toast-body">
              <span className="toast-title">Employee added — {toast.name}</span>
              <span className="toast-sub">{toast.message}</span>
            </div>
            <div className="toast-actions">
              <button
                className="toast-btn primary"
                onClick={() => openNewEmployee(toast.id)}
              >
                Open Profile
              </button>
            </div>
          </div>
        </>
      )}
    </Page>
  );
};

export default TeamManagement;
